<template>
  <div v-if="questionnaireActive" class="questionnaires-details-card">
    <v-card>
      <div class="questionnaire-details-card-header d-flex flex-row align-center pr-4 backdrop-item">
        <div class="d-flex flex-column justify-center flex-grow-1">
          <AdCardTitle key="title">
            <v-fade-transition group leave-absolute>
              <div key="questionnaireItemName" class="d-flex flex-row">
                <AdStatusDot darker larger :status="questionnaireActive.statusCode"
                  :icon="$filters.getQuestionnaireIcon(questionnaireActive)" />
                <span class="pl-2" style="white-space: nowrap;">{{ tructateText($filters.getMatchingTranslation({ value:
                  questionnaireActive.name }), 30) }}</span>
                <AdRefChip v-if="questionnaireActive.ref && questionnaireActive.ident" class="d-inline mt-1"
                  :ident="questionnaireActive.ident" darker small />
              </div>
            </v-fade-transition>
          </AdCardTitle>
        </div>
        <v-spacer />
        <!-- <AdTempleButton
          :id="questionnaireActive.id || questionnaireActive.ref"
          :effectiveDate="questionnaireActive.effectiveDate"
          :isEditable="questionnaireItemIsEditable && !questionnaireActive.ref"
        /> -->

        <AdArtefactDownloads v-model="modals.artefactDownload" :title="$filters.isQuestionnaireResponse(questionnaireItem) ? $t('download-questionnaire-response') : $t('download-questionnaire')"
          :downloadData="questionnaireDownloadData" />
        <AdButton type="download" @click="modals.artefactDownload = true" />

        <QuestionnaireRender v-model="modals.questionnaireRender" :questionnaire="questionnaireActive" />
        <AdButton type="render" @click="modals.questionnaireRender = true" />
        <IssueCreationModal v-if="userIsIssueEditor" :object="questionnaireActive" @create="handleCreateIssue" />
      </div>
      <v-card-text>

        <PanelLoading :loading="isLoading('questionnaireItem')" />

        <template v-if="!isLoading('questionnaireItem')">
          <EditableArtefactDetailHeader :item="questionnaireItem" :locales="projectLocales"
            :artefact="$filters.isQuestionnaireResponse(questionnaireItem) ? 'QuestionnaireResponse' : 'Questionnaire'"
            :service="patchMetaData" :readonly="!questionnaireItemIsEditable"
            :isEditableStatusCode="statusCodeIsEditable" :isEditableDesc="true"
            :hasDetails="!$filters.isQuestionnaireResponse(questionnaireItem)" :showMore.sync="metadataIsExpanded">
            <QuestionnaireMetaInformation :readonly="!questionnaireItemIsEditable" />
          </EditableArtefactDetailHeader>

          <QuestionnaireResponseRenderer v-if="$filters.isQuestionnaireResponse(questionnaireActive)" />
          <QuestionnaireBodyEditor v-else />

          <IssueAssociationsSection v-if="questionnaireItem && (questionnaireActive.id || questionnaireActive.ref)"
            ref="issueSection" :count="issueAssociations" :objectId="questionnaireItem.id || questionnaireItem.ref"
            :objectEffectiveDate="questionnaireItem.effectiveDate || null" />

          <RelationshipSection v-if="questionnaireItem && Array.isArray(questionnaireItem.relationship)"
            :expanded.sync="relationshipSectionIsExpanded" :item="questionnaireItem" objectType="questionnaire"
            readonly />
        </template>

      </v-card-text>
    </v-card>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import QuestionnaireMetaInformation from '@/components/Rule/QuestionnaireMetaInformation'
// import AdTempleButton from '@/components/Utilities/AdTempleButton'
import IssueCreationModal from '@/components/Issue/IssueCreationModal'
import EditableArtefactDetailHeader from '@/components/Utilities/Editable/EditableStructure/EditableArtefactDetailHeader'
import IssueAssociationsSection from '@/components/Utilities/IssueAssociationsSection'
import RelationshipSection from '@/components/Utilities/RelationshipSection'
import AdArtefactDownloads from '@/components/Rule/AdArtefactDownloads'
import QuestionnaireRender from '@/components/Rule/QuestionnaireRender'
import QuestionnaireBodyEditor from '@/components/Rule/QuestionnaireBodyEditor'
import QuestionnaireResponseRenderer from '@/components/Rule/QuestionnaireResponseRenderer'

export default {
  components: {
    IssueAssociationsSection,
    QuestionnaireMetaInformation,
    // AdTempleButton,
    IssueCreationModal,
    EditableArtefactDetailHeader,
    AdArtefactDownloads,
    QuestionnaireRender,
    QuestionnaireBodyEditor,
    RelationshipSection,
    QuestionnaireResponseRenderer
  },
  name: 'QuestionnaireDetailCard',
  data () {
    return {
      modals: {
        artefactDownload: false,
        questionnaireRender: false
      }
    }
  },
  computed: {
    ...mapGetters('questionnaire', ['questionnaires', 'questionnaireActive', 'questionnaireItem', 'questionnairesAreEditable', 'questionnaireItemIsEditable', 'isLoading']),
    ...mapGetters('server', ['decorStatusMaps']),
    ...mapGetters('project', ['projectHasFhirService']),

    issueAssociations () {
      return parseInt(this.questionnaireActive?.issueAssociation?.[0]?.count) || null
    },
    itemStatusCodeLifeCycle () {
      return this.decorStatusMaps?.ItemStatusCodeLifeCycle?.[0] || []
    },
    statusCodeIsEditable () {
      return this.userIsEditor &&
      this.$store.getters['server/getServerFunction']({ path: 'decor.rules.questionnaires', crudAction: 'write' }) &&
      this.itemStatusCodeLifeCycle[this.questionnaireActive?.statusCode]?.length > 1
    },
    headersQuestionnaireItems () {
      return [
        {
          text: this.$t('items'),
          value: 'text',
          width: 'auto'
        },
        {
          text: this.$t('Card'),
          value: 'required',
          width: '5%'
        }
      ]
    },
    getQuestionnaireItemIcon () {
      return item => {
        if (item.type === 'group') return 'mdi-folder'
        if (item.type === 'display') return 'mdi-tag-text-outline'
        return this.$filters.getValueDomainTypeIcon(item.type)
      }
    },
    getQuestionnaireItemCardinality () {
      return item => {
        const rq = (item.required === 'true') ? '1' : '0'
        const rp = (item.repeats === 'true') ? '*' : '1'
        return rq + '...' + rp
      }
    },
    questionnaireContent () {
      return this.questionnaireItem?.item || []
    },
    questionnaireDownloadData () {
      if (!this.questionnaireActive) return []
      return [
        {
          categoryTitle: this.$t('FHIR'),
          color: 'red',
          links: ['4.0', '4.3', '5.0'].flatMap(format => ([
            {
              text: `${this.$store.getters['server/getFhirLabel'](format)} - XML`,
              condition: () => this.$store.getters['project/projectHasFhirService'](format),
              href: this.getQuestionnaireDownloadLink(this.questionnaireActive, format)
            },
            {
              text: `${this.$store.getters['server/getFhirLabel'](format)} - JSON`,
              condition: () => this.$store.getters['project/projectHasFhirService'](format),
              href: this.getQuestionnaireDownloadLink(this.questionnaireActive, format, 'json')
            }
          ]))
        }
      ]
    },
    metadataIsExpanded: {
      get () {
        return this.$store.state.questionnaire.expandedSections.metadata
      },
      set (value) {
        this.$store.commit('questionnaire/setExpandedSection', { section: 'metadata', value })
      }
    },
    relationshipSectionIsExpanded: {
      get () {
        return this.$store.state.questionnaire.expandedSections.relationshipSection
      },
      set (value) {
        this.$store.commit('questionnaire/setExpandedSection', { section: 'relationshipSection', value })
      }
    }
  },
  methods: {
    fetchQuestionnaireByRouteParams () {
      // Set questionnaireActive based on the route params
      const { id } = this.$route.params
      if (!id) {
        return
      }
      const activeQuestionnaire = this.$filters.listHelper.findItemInList({
        items: this.questionnaires || [],
        children: 'questionnaireresponse',
        handler: match => match.id === id || match.ref === id
      })
      if (!activeQuestionnaire) {
        return
      }

      this.$store.commit('questionnaire/setQuestionnaireItem', null)
      this.$store.commit('questionnaire/setQuestionnaireActive', activeQuestionnaire)

      if (this.$route.params.questionnairePanel && id === this.$route.params.questionnairePanel.questionnaireItem.id) {
        // The user navigated to this page from the Terminology Browser. The questionnaireItem is passed through route props, so no need for fetching.
        this.$store.commit('questionnaire/setQuestionnaireItem', this.$route.params.questionnairePanel.questionnaireItem)
        return
      }

      if (this.$filters.isQuestionnaireResponse(activeQuestionnaire)) {
        this.$store.commit('questionnaire/setQuestionnaireItem', activeQuestionnaire)
        return // For QuestionnaireResponses, there is nothing more to fetch right now.
      }

      this.$store.dispatch('questionnaire/getQuestionnaire', {
        id: activeQuestionnaire.id || activeQuestionnaire.ref,
        effectiveDate: activeQuestionnaire.effectiveDate,
        QR: false
      }).catch(error => {
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || error?.message || 'Error'
        this.$oops(errorMessage)
      })
    },
    getQuestionnaireDownloadLink (item, fhirFormat, dataFormat = 'xml') {
      const BASE_URL = this.$store.getters['project/getFhirURL'](fhirFormat)
      const { id, effectiveDate, ident } = item
      const prefix = this.projectInFocus?.prefix
      const parsedEffectiveDate = effectiveDate.replaceAll(/[-:T]/g, '')
      // For QR's, the effectiveDate exists only in the wrapper. It cannot be used to fetch the QR. See AD30-1251.
      const url = this.$filters.isQuestionnaireResponse(item)
        ? `${BASE_URL}/${fhirFormat}/${ident || prefix}/QuestionnaireResponse/${id}?_format=${dataFormat}`
        : `${BASE_URL}/${fhirFormat}/${ident || prefix}/Questionnaire/${id}--${parsedEffectiveDate}?_format=${dataFormat}`
      return url
    },
    handleCreateIssue (issue) {
      if (this.$refs?.issueSection?.issueList?.issue) {
        this.$refs.issueSection.issueList.issue.push(issue)
      }
    },
    htmlToText (htmlString) {
      const el = document.createElement('div')
      el.innerHTML = htmlString
      return el.textContent
    },
    patchMetaData (item, data) {
      return this.$store.dispatch('questionnaire/patchQuestionnaire', { item, data })
        .catch((error) => {
          const errorMessage = error?.response?.data?.description || error?.response?.statusText || error || 'Error'
          this.$oops(errorMessage)
        })
    },
    tructateText (text, length = 100) {
      const truncated = text.substr(0, length)
      return text.length > truncated.length
        ? `${truncated}...`
        : text
    }
  },
  beforeRouteEnter (to, from, next) {
    next(async vm => {
      try {
        if (vm.$store.getters['questionnaire/questionnaires'] === null) {
          await vm.$store.dispatch('questionnaire/getQuestionnaireList')
        }
        vm.fetchQuestionnaireByRouteParams()
      } catch (error) {
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
        vm.$oops(errorMessage)
      }
    })
  },
  async beforeRouteUpdate (to, from, next) {
    // Check if the questionnaire list is fetched
    if (this.$store.getters['questionnaire/questionnaires'] === null) {
      await this.$store.dispatch('questionnaire/getQuestionnaireList')
    }
    const currentId = this.questionnaireItem?.id || this.questionnaireItem?.ref
    const currentEffectiveDate = this.questionnaireItem?.effectiveDate

    // Check if the route params match with the currently fetched questionnaireItem. If not, another questionnaire should be fetched
    if (to.params.id === currentId && to.params.effectiveDate === currentEffectiveDate) {
      return next()
    }

    // Look for the requested questionnaire based on the route params
    const questionnaire = this.$filters.listHelper.findItemInList({
      items: this.questionnaires || [],
      children: 'questionnaireresponse',
      handler: t => t.id === to.params.id || t.ref === to.params.id
    })
    if (!questionnaire) {
      this.$store.commit('questionnaire/setQuestionnaireActive', null)
      this.$store.commit('questionnaire/setQuestionnaireItem', null)
      return next()
    }

    // If id in route doesnt't match with questionnaireItem and questionnaireActive, set questionnaireActive
    if (to.params.id !== currentId && to.params.id !== this.questionnaireActive?.id) {
      this.$store.commit('questionnaire/setQuestionnaireActive', questionnaire)
    }

    if (this.$filters.isQuestionnaireResponse(questionnaire)) {
      this.$store.commit('questionnaire/setQuestionnaireItem', questionnaire)
      return next() // For QuestionnaireResponses, there is nothing more to fetch right now.
    }

    // Fetch the questionnaire
    this.$store.dispatch('questionnaire/getQuestionnaire', {
      id: questionnaire.id || questionnaire.ref,
      effectiveDate: questionnaire.effectiveDate,
      QR: false
    }).catch(error => {
      const errorMessage = error?.response?.data?.description || error?.response?.statusText || error.message || 'Error'
      this.$oops(errorMessage)
    })

    next()
  }
}
</script>

<style lang="scss" scoped>
  .questionnaires-details-card {
    position: relative;
    top: 0;
  }

  .questionnaire-details-card-header {
    position: sticky;
    top: 48px;
    z-index: 4;

    height: 72px;
    background: var(--v-topbottomstrip-base);

    ::v-deep .v-card__title {
      background: transparent !important;
    }

    @media screen and (max-width: 1264px) {
      ::v-deep .v-btn__content {
        span {
          display: none;
        }
        i {
          margin: 0;
          font-size: 26px !important;
        }
      }
    }
  }

</style>
